/*************************************************

* BAKER HUGHES COMPANY CONFIDENTIAL

* Unpublished work copyright 2025 Baker Hughes Company.  All rights reserved.

*

* This computer code is proprietary and highly confidential to Baker Hughes Company

* and/or its affiliate(s). It may not be used, disclosed, modified, transferred,

* or reproduced without prior written consent, and must be returned on demand.

**************************************************/
import {
  ScmIntegrationsApi,
  scmIntegrationsApiRef,
  ScmAuth,
} from '@backstage/integration-react';

import {
  AnyApiFactory,
  ApiRef,
  OpenIdConnectApi,
  configApiRef,
  createApiFactory,
  discoveryApiRef,
  identityApiRef,
  ProfileInfoApi,
  BackstageIdentityApi,
  SessionApi,
  createApiRef,
  oauthRequestApiRef
} from '@backstage/core-plugin-api';

import {
  OpenApiDefinitionWidget,
  apiDocsConfigRef,
  defaultDefinitionWidgets,
} from '@backstage/plugin-api-docs';
import { ApiEntity } from '@backstage/catalog-model';

import { signalApiRef } from '@backstage/plugin-signals-react';
import { SignalClient } from '@backstage/plugin-signals';
import { OAuth2 } from '@backstage/core-app-api';

// Keycloak
export const kcOIDCAuthApiRef: ApiRef<
  OpenIdConnectApi & ProfileInfoApi & BackstageIdentityApi & SessionApi
> = createApiRef({
  id: 'auth.keycloak',
});

// Enhanced Keycloak configuration with security improvements
export const createKeycloakAuthApi = (configApi: any, discoveryApi: any, oauthRequestApi: any) => {
  return OAuth2.create({
    configApi,
    discoveryApi,
    oauthRequestApi,
    provider: {
      id: 'keycloak',
      title: 'Log in with Keycloak',
      icon: () => null,
    },
    environment: configApi.getOptionalString('auth.environment'),
    defaultScopes: ['openid', 'profile', 'email'], // Removed 'roles' scope
    
    // Enhanced security options
    popupOptions: {
      size: {
        fullscreen: true,
      },
    },
    // Remove sessionOptions - not a valid OAuth2.create() property
    // Token refresh is handled automatically by OAuth2 implementation
  });
};

export const apis: AnyApiFactory[] = [
  createApiFactory({
    api: scmIntegrationsApiRef,
    deps: { configApi: configApiRef },
    factory: ({ configApi }) => ScmIntegrationsApi.fromConfig(configApi),
  }),
  ScmAuth.createDefaultApiFactory(),
  // @ts-ignore
  createApiFactory({
    api: apiDocsConfigRef,
    deps: { configApi: configApiRef },
    factory: ({ configApi }) => {
      // Overriding openapi definition widget to add header
      const requestInterceptor = (req: any) => {
        const reqURL = new URL(req.url);
        const baseUrl = configApi.getString('backend.baseUrl');
        req.url = `${baseUrl}/api/swagger-proxy${reqURL.pathname}${reqURL.search}`;
        return req;
      };
      const definitionWidgets = defaultDefinitionWidgets().map(obj => {
        if (obj.type === 'openapi') {
          return {
            ...obj,
            component: (definition: string) => (
              <OpenApiDefinitionWidget
                definition={definition}
                requestInterceptor={requestInterceptor}
              />
            ),
          };
        }
        return obj;
      });

      return {
        getApiDefinitionWidget: (apiEntity: ApiEntity) => {
          return definitionWidgets.find(
            (d: { type: string }) => d.type === apiEntity.spec.type,
          );
        },
      };
    },
  }),
  createApiFactory({
    api: signalApiRef,
    deps: {
      identity: identityApiRef,
      discoveryApi: discoveryApiRef,
    },
    factory: ({ identity, discoveryApi }) => {
      return SignalClient.create({
        identity,
        discoveryApi,
      });
    },
  }),
   createApiFactory({
    api: kcOIDCAuthApiRef,
    deps: {
      discoveryApi: discoveryApiRef,
      oauthRequestApi: oauthRequestApiRef,
      configApi: configApiRef,
    },
    factory: ({ discoveryApi, oauthRequestApi, configApi }) =>
      createKeycloakAuthApi(configApi, discoveryApi, oauthRequestApi),
  }),
  
];
